/**
 * useGoogleAnalytics Hook
 *
 * Central hook providing all GTM event dispatch functions.
 * Returns no-op functions when GTM is disabled or on the server.
 */

'use client';

import type { ProductCardData } from '@/components/brand';
import type { CheckoutLine } from '@/states/checkout/checkoutState';
import { FEATURES, GTM_ID } from '@/lib/const';
import { EVENT_NAME, GA_ECOMMERCE_EVENT_DELAY_MS, PAGE_TYPE } from '@/lib/gtm/const';
import {
  buildItemFromProduct,
  buildItemFromProductCard,
  checkoutLineToGtmInput,
  fetchEcommerceByLines,
  fetchEcommerceBySkus,
  omitEcommerceValueAndCurrency,
  resolveGtmLines,
  stripItemBrandFromEcommercePayload,
  type GtmCheckoutLineSource,
} from '@/lib/gtm/ecommerce';
import type { GtmLineInput } from '@/lib/gtm/ecommerceSchemas';
import { pushEvent } from '@/lib/gtm/eventQueue';
import { consumePendingPageViewExtras } from '@/lib/gtm/pageViewExtras';
import { isPreOrderCta, stampPreOrderItems } from '@/lib/gtm/preOrder';
import { type PromotionParams } from '@/lib/gtm/promotion';
import {
  getCountryFromLocale,
  getCountryIP,
  getLanguageFromLocale,
  getMenuCategory,
  getPageType,
  getUserFromCookie,
  hashEmail,
  isDebugMode,
  mapAppointmentMotiveForGtm,
  resolveMarketEcom,
} from '@/lib/gtm/helpers';
import type {
  BaseEvent,
  ContactEvent,
  CtaDiscoverEvent,
  EcommerceEvent,
  EcommercePayload,
  Item,
  NewsletterSubscriptionEvent,
  PopinCloseEvent,
  PopinViewEvent,
  SearchEvent,
  VideoEvent,
  GTMEvent,
} from '@/lib/gtm/types';
import type { Product } from '@/lib/product/types';
import { devError } from '@/lib/utils/devLog';
import { useEcommerceReady } from '@/providers/ecommerceReadyProvider';
import { pageReferrerState } from '@/states/pageReferrerState';
import { useLocale } from 'next-intl';
import { usePathname } from 'next/navigation';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSnapshot } from 'valtio';

type VideoEventName = 'video_start' | 'video_progress' | 'video_complete';
type EcommerceEventName = (typeof EVENT_NAME)[keyof typeof EVENT_NAME];

interface GoogleAnalyticsHook {
  sendPageView: () => void;
  sendPopinEvent: (popinTitle: string, isOpen: boolean) => void;
  sendNewsletterEvent: (country: string, language: string) => void;
  sendCtaDiscoverEvent: (ctaName: string, ctaTitle: string) => void;
  sendVideoEvent: (eventName: VideoEventName, title: string, url: string, percent: number) => void;
  sendSearchEvent: (searchTerm: string) => void;
  sendLoginEvent: () => void;
  sendSignUpEvent: (optIn?: boolean) => void;
  sendContactEvent: (contactType: 'phone' | 'email') => void;
  sendCallToBuyEvent: (sku: string) => Promise<void>;
  sendGenerateLead: (params: {
    appointmentMotive?: string;
    appointmentId?: string;
    storeId?: string;
    dateAppointment?: string;
    sku?: string;
  }) => Promise<void>;
  sendBookAnAppt: () => void;
  sendNotifyMeEvent: (itemId: string) => Promise<void>;
  sendAvailabilityRequestEvent: (
    sku: string,
    phase: 'submit' | 'success',
    storeId?: string
  ) => Promise<void>;
  sendPromotionEvent: (
    event: typeof EVENT_NAME.VIEW_PROMOTION | typeof EVENT_NAME.SELECT_PROMOTION,
    params: PromotionParams
  ) => Promise<void>;
  sendIntentionEvent: (
    event:
      | typeof EVENT_NAME.INTENTION_BOOK_APPT
      | typeof EVENT_NAME.INTENTION_REGISTER_WATCH
      | typeof EVENT_NAME.INTENTION_WARRANTY_EXTENSION
      | typeof EVENT_NAME.INTENTION_AVAILABILITY_REQUEST
  ) => void;
  sendViewItem: (product: Product) => void;
  sendViewItemList: (products: ProductCardData[], itemListName?: string) => void;
  sendSelectItem: (product: ProductCardData, index?: number, itemListName?: string) => void;
  sendAddToCart: (product: ProductCardData | Product) => void;
  sendRemoveFromCart: (
    checkoutLines: ReadonlyArray<GtmCheckoutLineSource> | undefined,
    optimisticLines: ReadonlyArray<CheckoutLine>,
    removedLineId?: string
  ) => Promise<void>;
  sendIntentionViewCart: (
    checkoutLines: ReadonlyArray<GtmCheckoutLineSource> | undefined,
    optimisticLines: ReadonlyArray<CheckoutLine>
  ) => Promise<void>;
  sendViewCart: (
    checkoutLines: ReadonlyArray<GtmCheckoutLineSource> | undefined,
    optimisticLines: ReadonlyArray<CheckoutLine>,
    totalValue?: number,
    eventExtras?: Record<string, unknown>
  ) => Promise<void>;
  sendBeginCheckout: (
    checkoutLines: ReadonlyArray<GtmCheckoutLineSource> | undefined,
    optimisticLines: ReadonlyArray<CheckoutLine>,
    totalValue?: number,
    eventExtras?: Record<string, unknown>
  ) => Promise<void>;
  sendAddShippingInfo: (
    checkoutLines: ReadonlyArray<GtmCheckoutLineSource> | undefined,
    optimisticLines: ReadonlyArray<CheckoutLine>,
    shippingTier: string,
    isGift?: boolean,
    totalValue?: number,
    eventExtras?: Record<string, unknown>
  ) => Promise<void>;
  sendAddPaymentInfo: (
    checkoutLines: ReadonlyArray<GtmCheckoutLineSource> | undefined,
    optimisticLines: ReadonlyArray<CheckoutLine>,
    paymentTypes: string[],
    totalValue?: number,
    currency?: string,
    eventExtras?: Record<string, unknown>
  ) => Promise<void>;
  sendReviewOrder: (
    checkoutLines: ReadonlyArray<GtmCheckoutLineSource> | undefined,
    optimisticLines: ReadonlyArray<CheckoutLine>,
    totalValue?: number
  ) => Promise<void>;
  sendPurchase: (params: {
    checkoutLines: ReadonlyArray<GtmCheckoutLineSource> | undefined;
    optimisticLines: ReadonlyArray<CheckoutLine>;
    transactionId: string;
    value: number;
    currency: string;
    tax?: number;
    shipping?: number;
    paymentTypes?: string[];
    coupon?: string;
    eventExtras?: Record<string, unknown>;
    omitLineItems?: boolean;
  }) => Promise<void>;
  sendGuestCheckout: () => void;
  sendEcommerceFromCheckoutLines: (params: {
    coupon?: string;
    event: EcommerceEventName;
    lines: ReadonlyArray<GtmCheckoutLineSource>;
    totalValue?: number;
    eventExtras?: Record<string, unknown>;
  }) => Promise<void>;
  sendEcommerceEventBySkus: (params: {
    event: EcommerceEventName;
    skus: string[];
    itemListName?: string;
    itemIndex?: number;
    eventExtras?: Record<string, unknown>;
    /** SKUs that should receive `order_type: PRE_ORDER` on items[] */
    preOrderSkus?: string[];
  }) => Promise<void>;
}

const NOOP_HOOK: GoogleAnalyticsHook = {
  sendPageView: () => {},
  sendPopinEvent: () => {},
  sendNewsletterEvent: () => {},
  sendCtaDiscoverEvent: () => {},
  sendVideoEvent: () => {},
  sendSearchEvent: () => {},
  sendLoginEvent: () => {},
  sendSignUpEvent: () => {},
  sendContactEvent: () => {},
  sendCallToBuyEvent: async () => {},
  sendGenerateLead: async () => {},
  sendBookAnAppt: () => {},
  sendNotifyMeEvent: async () => {},
  sendAvailabilityRequestEvent: async () => {},
  sendPromotionEvent: async () => {},
  sendIntentionEvent: () => {},
  sendViewItem: () => {},
  sendViewItemList: () => {},
  sendSelectItem: () => {},
  sendAddToCart: () => {},
  sendRemoveFromCart: async () => {},
  sendIntentionViewCart: async () => {},
  sendViewCart: async () => {},
  sendBeginCheckout: async () => {},
  sendAddShippingInfo: async () => {},
  sendAddPaymentInfo: async () => {},
  sendReviewOrder: async () => {},
  sendPurchase: async () => {},
  sendGuestCheckout: () => {},
  sendEcommerceFromCheckoutLines: async () => {},
  sendEcommerceEventBySkus: async () => {},
};

function isProductCard(product: ProductCardData | Product): product is ProductCardData {
  return 'href' in product;
}

export function useGoogleAnalytics(): GoogleAnalyticsHook {
  const locale = useLocale();
  const pathname = usePathname();
  const { previousUrl } = useSnapshot(pageReferrerState);
  const { ready: ecommerceReady, source: ecommerceSource } = useEcommerceReady();

  const isEnabled = FEATURES.gtmEnabled && GTM_ID !== '';

  const [hashedEmail, setHashedEmail] = useState<string | undefined>(undefined);
  const prevEmailRef = useRef<string | undefined>(undefined);
  const pushedListSkusRef = useRef<Set<string>>(new Set());
  const viewItemTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const viewItemListTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearDelayedEcommerceTimers = useCallback(() => {
    if (viewItemTimerRef.current !== null) {
      clearTimeout(viewItemTimerRef.current);
      viewItemTimerRef.current = null;
    }
    if (viewItemListTimerRef.current !== null) {
      clearTimeout(viewItemListTimerRef.current);
      viewItemListTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    clearDelayedEcommerceTimers();
  }, [pathname, clearDelayedEcommerceTimers]);

  useEffect(() => {
    return () => {
      clearDelayedEcommerceTimers();
    };
  }, [clearDelayedEcommerceTimers]);

  useEffect(() => {
    if (!isEnabled) return;
    const { email } = getUserFromCookie();
    if (email === prevEmailRef.current) return;
    prevEmailRef.current = email;
    hashEmail(email).then(setHashedEmail);
  }, [isEnabled, pathname]);

  const buildBaseEvent = useCallback((): Omit<BaseEvent, 'event'> => {
    const country = getCountryFromLocale(locale);
    const countryIP = getCountryIP();
    const { salesForceId, email } = getUserFromCookie();
    const marketEcom = resolveMarketEcom(ecommerceSource, ecommerceReady, country);
    return {
      country,
      ...(countryIP && { countryIP }),
      language: getLanguageFromLocale(locale),
      market_ecom: marketEcom,
      menu_category: getMenuCategory(pathname),
      page_type: getPageType(pathname),
      page_referrer:
        previousUrl || (globalThis.document === undefined ? '' : globalThis.document.referrer),
      ...(salesForceId && { user_id: salesForceId }),
      ...(hashedEmail && { user_hashed_email: hashedEmail }),
      user_status: email ? 'yes' : 'no',
      ...(isDebugMode() && { debug_mode: true }),
    };
  }, [locale, pathname, previousUrl, hashedEmail, ecommerceReady, ecommerceSource]);

  const sendEcommerceEvent = useCallback(
    (data: Partial<EcommerceEvent>) => {
      if (!isEnabled || !data.event) return;
      pushEvent(
        { ecommerce: null } as EcommerceEvent,
        false /* requiresInit: bypass page_view queue */
      );
      const { ecommerce, ...rest } = data;
      const sanitizedEcommerce = ecommerce
        ? stripItemBrandFromEcommercePayload(ecommerce)
        : undefined;
      pushEvent({
        ...buildBaseEvent(),
        ...rest,
        ...(sanitizedEcommerce !== undefined && { ecommerce: sanitizedEcommerce }),
        event: data.event,
      } as EcommerceEvent);
    },
    [isEnabled, buildBaseEvent]
  );

  const sendEvent = useCallback(
    (data: Partial<GTMEvent>) => {
      if (!isEnabled || !data.event) return;
      pushEvent({
        ...buildBaseEvent(),
        ...data,
        event: data.event,
      } as GTMEvent);
    },
    [isEnabled, buildBaseEvent]
  );

  const sendPageView = useCallback(() => {
    if (!isEnabled) return;
    clearDelayedEcommerceTimers();
    pushedListSkusRef.current.clear();
    pushEvent({
      ...buildBaseEvent(),
      ...consumePendingPageViewExtras(),
      event: EVENT_NAME.PAGE_VIEW,
    });
  }, [isEnabled, buildBaseEvent, clearDelayedEcommerceTimers]);

  const sendPopinEvent = useCallback(
    (popinTitle: string, isOpen: boolean) => {
      if (!isEnabled) return;
      const event = isOpen ? EVENT_NAME.POPIN_VIEW : EVENT_NAME.POPIN_CLOSE;
      pushEvent({
        ...buildBaseEvent(),
        event,
        popin_title: popinTitle,
      } as PopinViewEvent | PopinCloseEvent);
    },
    [isEnabled, buildBaseEvent]
  );

  const sendNewsletterEvent = useCallback(
    (country: string, language: string) => {
      if (!isEnabled) return;
      pushEvent({
        ...buildBaseEvent(),
        event: EVENT_NAME.NEWSLETTER_SUBSCRIPTION,
        newsletter_country: country.toUpperCase(),
        newsletter_language: language.toLowerCase(),
      } as NewsletterSubscriptionEvent);
    },
    [isEnabled, buildBaseEvent]
  );

  const sendCtaDiscoverEvent = useCallback(
    (ctaName: string, ctaTitle: string) => {
      if (!isEnabled) return;
      pushEvent({
        ...buildBaseEvent(),
        event: EVENT_NAME.CTA_DISCOVER,
        click_cta_name: ctaName,
        cta_title: ctaTitle,
      } as CtaDiscoverEvent);
    },
    [isEnabled, buildBaseEvent]
  );

  const sendVideoEvent = useCallback(
    (eventName: VideoEventName, title: string, url: string, percent: number) => {
      if (!isEnabled) return;
      pushEvent({
        ...buildBaseEvent(),
        event: eventName,
        video_title: title,
        video_url: url,
        video_percent: percent,
      } as VideoEvent);
    },
    [isEnabled, buildBaseEvent]
  );

  const sendSearchEvent = useCallback(
    (searchTerm: string) => {
      if (!isEnabled) return;
      sendEvent({
        event: EVENT_NAME.SEARCH,
        search_term: searchTerm,
      } as SearchEvent);
    },
    [isEnabled, sendEvent]
  );

  const sendLoginEvent = useCallback(() => {
    if (!isEnabled) return;
    sendEvent({
      event: EVENT_NAME.LOGIN,
      page_type: PAGE_TYPE.LOGIN_OR_ACCOUNT_CREATION,
      account_authentification: 'regular',
      login_type: 'regular',
    });
  }, [isEnabled, sendEvent]);

  const sendSignUpEvent = useCallback(
    (optIn = false) => {
      if (!isEnabled) return;
      sendEvent({
        event: EVENT_NAME.SIGN_UP,
        page_type: PAGE_TYPE.ACCOUNT_CREATION_FORM,
        account_authentification: 'regular',
        opt_in: optIn ? 'yes' : 'no',
      });
    },
    [isEnabled, sendEvent]
  );

  const sendContactEvent = useCallback(
    (contactType: 'phone' | 'email') => {
      if (!isEnabled) return;
      sendEvent({
        event: EVENT_NAME.CONTACT,
        contact_type: contactType,
      });
    },
    [isEnabled, sendEvent]
  );

  const sendGenerateLead = useCallback(
    async ({
      appointmentMotive,
      appointmentId,
      storeId,
      dateAppointment,
      sku,
    }: {
      appointmentMotive?: string;
      appointmentId?: string;
      storeId?: string;
      dateAppointment?: string;
      sku?: string;
    }) => {
      if (!isEnabled) return;

      let items: Item[] = [];
      if (sku) {
        try {
          const ecommerce = await fetchEcommerceBySkus({ locale, skus: [sku] });
          items = ecommerce.items ?? [];
        } catch (error) {
          devError('[GTM] generate_lead enrichment failed:', error);
        }
      }

      pushEvent({ ecommerce: null } as EcommerceEvent, false);
      pushEvent({
        ...buildBaseEvent(),
        event: EVENT_NAME.GENERATE_LEAD,
        ...(appointmentMotive
          ? { appointment_motive: mapAppointmentMotiveForGtm(appointmentMotive) }
          : {}),
        ...(appointmentId ? { appointment_id: appointmentId } : {}),
        ...(storeId ? { store_id: storeId } : {}),
        ...(dateAppointment ? { date_appointment: dateAppointment } : {}),
        ecommerce: stripItemBrandFromEcommercePayload({ items }),
      });
    },
    [isEnabled, locale, buildBaseEvent]
  );

  const sendBookAnAppt = useCallback(() => {
    if (!isEnabled) return;
    sendEvent({ event: EVENT_NAME.BOOK_AN_APPT });
  }, [isEnabled, sendEvent]);

  const sendPromotionEvent = useCallback(
    async (
      event: typeof EVENT_NAME.VIEW_PROMOTION | typeof EVENT_NAME.SELECT_PROMOTION,
      params: PromotionParams
    ) => {
      if (!isEnabled) return;
      try {
        let items: Item[] = [];

        if (params.sku) {
          const ecommerce = await fetchEcommerceBySkus({ locale, skus: [params.sku] });
          items = ecommerce.items ?? [];
        }

        sendEcommerceEvent({
          event,
          ecommerce: {
            creative_name: params.creativeName,
            creative_slot: params.creativeSlot,
            promotion_id: params.promotionId,
            promotion_name: params.promotionName,
            items,
          },
        });
      } catch (error) {
        devError('[GTM] sendPromotionEvent failed:', error);
      }
    },
    [isEnabled, locale, sendEcommerceEvent]
  );

  const sendIntentionEvent = useCallback(
    (
      event:
        | typeof EVENT_NAME.INTENTION_BOOK_APPT
        | typeof EVENT_NAME.INTENTION_REGISTER_WATCH
        | typeof EVENT_NAME.INTENTION_WARRANTY_EXTENSION
        | typeof EVENT_NAME.INTENTION_AVAILABILITY_REQUEST
    ) => {
      if (!isEnabled) return;
      sendEvent({
        event,
        ...(event === EVENT_NAME.INTENTION_REGISTER_WATCH && {
          page_type: PAGE_TYPE.MY_ACCOUNT_MY_WATCHES,
        }),
      });
    },
    [isEnabled, sendEvent]
  );

  const sendViewItem = useCallback(
    (product: Product) => {
      if (!isEnabled) return;
      const fire = () => {
        sendEcommerceEvent({
          event: EVENT_NAME.VIEW_ITEM,
          ecommerce: {
            items: [buildItemFromProduct(product)],
          },
        });
      };
      if (viewItemTimerRef.current !== null) {
        clearTimeout(viewItemTimerRef.current);
      }
      viewItemTimerRef.current = setTimeout(() => {
        viewItemTimerRef.current = null;
        fire();
      }, GA_ECOMMERCE_EVENT_DELAY_MS);
    },
    [isEnabled, sendEcommerceEvent]
  );

  const sendSelectItem = useCallback(
    (product: ProductCardData, index = 0, itemListName?: string) => {
      if (!isEnabled) return;
      sendEcommerceEvent({
        event: EVENT_NAME.SELECT_ITEM,
        ecommerce: {
          items: [buildItemFromProductCard(product, index, itemListName)],
        },
      });
    },
    [isEnabled, sendEcommerceEvent]
  );

  const sendAddToCart = useCallback(
    (product: ProductCardData | Product) => {
      if (!isEnabled) return;
      const item = isProductCard(product)
        ? buildItemFromProductCard(product)
        : buildItemFromProduct(product);

      sendEcommerceEvent({
        event: EVENT_NAME.ADD_TO_CART,
        ecommerce: {
          currency: item.currency,
          value: item.price * item.quantity,
          items: [item],
        },
      });
    },
    [isEnabled, sendEcommerceEvent]
  );

  const sendEcommerceByLines = useCallback(
    async ({
      event,
      lines,
      totalValue,
      coupon,
      includeCurrency = true,
      extra,
      giftOption,
      eventExtras,
      omitLineItems,
    }: {
      event: EcommerceEventName;
      lines: GtmLineInput[];
      totalValue?: number;
      coupon?: string;
      includeCurrency?: boolean;
      extra?: Partial<EcommercePayload>;
      giftOption?: 'yes' | 'no';
      eventExtras?: Record<string, unknown>;
      omitLineItems?: boolean;
    }) => {
      if (!isEnabled || (lines.length === 0 && !omitLineItems)) return;
      try {
        const ecommerce = await fetchEcommerceByLines({
          locale,
          lines: omitLineItems ? [] : lines,
          totalValue,
          coupon,
          includeCurrency,
        });
        const stampAllPreOrder =
          !omitLineItems &&
          eventExtras?.order_type === 'PRE_ORDER' &&
          (ecommerce.items?.length ?? 0) > 0;
        const items = stampAllPreOrder ? stampPreOrderItems(ecommerce.items) : ecommerce.items;
        const emailHash = hashedEmail ?? (await hashEmail(getUserFromCookie().email));
        sendEcommerceEvent({
          event,
          ...(giftOption && { gift_option: giftOption }),
          ...eventExtras,
          ...(emailHash && { user_hashed_email: emailHash }),
          ecommerce: {
            ...ecommerce,
            ...extra,
            ...(omitLineItems ? { items: undefined } : { items }),
          },
        });
      } catch (error) {
        devError(`[GTM] ${event} failed:`, error);
      }
    },
    [isEnabled, locale, sendEcommerceEvent, hashedEmail]
  );

  /** Public API: accepts Saleor checkout lines, maps them, then enriches via BFF. */
  const sendEcommerceFromCheckoutLines = useCallback(
    async ({
      coupon,
      event,
      lines,
      totalValue,
      eventExtras,
    }: {
      coupon?: string;
      event: EcommerceEventName;
      lines: ReadonlyArray<GtmCheckoutLineSource>;
      totalValue?: number;
      eventExtras?: Record<string, unknown>;
    }) => {
      const gtmLines = lines.map(checkoutLineToGtmInput);
      await sendEcommerceByLines({
        event,
        lines: gtmLines,
        totalValue,
        coupon,
        eventExtras,
        includeCurrency: event !== EVENT_NAME.INTENTION_VIEW_CART,
      });
    },
    [sendEcommerceByLines]
  );

  const sendRemoveFromCart = useCallback(
    async (
      checkoutLines: ReadonlyArray<GtmCheckoutLineSource> | undefined,
      optimisticLines: ReadonlyArray<CheckoutLine>,
      removedLineId?: string
    ) => {
      const allLines = resolveGtmLines(checkoutLines, optimisticLines);
      const targetLines = removedLineId
        ? allLines.filter((line) => line.id === removedLineId)
        : allLines;
      await sendEcommerceByLines({
        event: EVENT_NAME.REMOVE_FROM_CART,
        lines: targetLines,
      });
    },
    [sendEcommerceByLines]
  );

  const sendIntentionViewCart = useCallback(
    async (
      checkoutLines: ReadonlyArray<GtmCheckoutLineSource> | undefined,
      optimisticLines: ReadonlyArray<CheckoutLine>
    ) => {
      await sendEcommerceByLines({
        event: EVENT_NAME.INTENTION_VIEW_CART,
        lines: resolveGtmLines(checkoutLines, optimisticLines),
        includeCurrency: false,
      });
    },
    [sendEcommerceByLines]
  );

  const sendViewCart = useCallback(
    async (
      checkoutLines: ReadonlyArray<GtmCheckoutLineSource> | undefined,
      optimisticLines: ReadonlyArray<CheckoutLine>,
      totalValue?: number,
      eventExtras?: Record<string, unknown>
    ) => {
      await sendEcommerceByLines({
        event: EVENT_NAME.VIEW_CART,
        lines: resolveGtmLines(checkoutLines, optimisticLines),
        totalValue,
        eventExtras,
      });
    },
    [sendEcommerceByLines]
  );

  const sendBeginCheckout = useCallback(
    async (
      checkoutLines: ReadonlyArray<GtmCheckoutLineSource> | undefined,
      optimisticLines: ReadonlyArray<CheckoutLine>,
      totalValue?: number,
      eventExtras?: Record<string, unknown>
    ) => {
      await sendEcommerceByLines({
        event: EVENT_NAME.BEGIN_CHECKOUT,
        lines: resolveGtmLines(checkoutLines, optimisticLines),
        totalValue,
        eventExtras,
      });
    },
    [sendEcommerceByLines]
  );

  const sendAddShippingInfo = useCallback(
    async (
      checkoutLines: ReadonlyArray<GtmCheckoutLineSource> | undefined,
      optimisticLines: ReadonlyArray<CheckoutLine>,
      shippingTier: string,
      isGift = false,
      totalValue?: number,
      eventExtras?: Record<string, unknown>
    ) => {
      await sendEcommerceByLines({
        event: EVENT_NAME.ADD_SHIPPING_INFO,
        lines: resolveGtmLines(checkoutLines, optimisticLines),
        totalValue,
        giftOption: isGift ? 'yes' : 'no',
        extra: { shipping_tier: shippingTier },
        eventExtras,
      });
    },
    [sendEcommerceByLines]
  );

  const sendAddPaymentInfo = useCallback(
    async (
      checkoutLines: ReadonlyArray<GtmCheckoutLineSource> | undefined,
      optimisticLines: ReadonlyArray<CheckoutLine>,
      paymentTypes: string[],
      totalValue?: number,
      currency?: string,
      eventExtras?: Record<string, unknown>
    ) => {
      await sendEcommerceByLines({
        event: EVENT_NAME.ADD_PAYMENT_INFO,
        lines: resolveGtmLines(checkoutLines, optimisticLines),
        totalValue,
        extra: { payment_type: paymentTypes, ...(currency && { currency }) },
        eventExtras,
      });
    },
    [sendEcommerceByLines]
  );

  const sendReviewOrder = useCallback(
    async (
      checkoutLines: ReadonlyArray<GtmCheckoutLineSource> | undefined,
      optimisticLines: ReadonlyArray<CheckoutLine>,
      totalValue?: number
    ) => {
      await sendEcommerceByLines({
        event: EVENT_NAME.REVIEW_ORDER,
        lines: resolveGtmLines(checkoutLines, optimisticLines),
        totalValue,
      });
    },
    [sendEcommerceByLines]
  );

  const sendPurchase = useCallback(
    async ({
      checkoutLines,
      optimisticLines,
      transactionId,
      value,
      currency,
      tax,
      shipping = 0,
      paymentTypes,
      coupon,
      eventExtras,
      omitLineItems,
    }: {
      checkoutLines: ReadonlyArray<GtmCheckoutLineSource> | undefined;
      optimisticLines: ReadonlyArray<CheckoutLine>;
      transactionId: string;
      value: number;
      currency: string;
      tax?: number;
      shipping?: number;
      paymentTypes?: string[];
      coupon?: string;
      eventExtras?: Record<string, unknown>;
      omitLineItems?: boolean;
    }) => {
      await sendEcommerceByLines({
        event: EVENT_NAME.PURCHASE,
        lines: resolveGtmLines(checkoutLines, optimisticLines),
        totalValue: value,
        coupon,
        eventExtras,
        omitLineItems,
        extra: {
          transaction_id: transactionId,
          currency,
          tax,
          shipping,
          payment_type: paymentTypes,
        },
      });
    },
    [sendEcommerceByLines]
  );

  const sendGuestCheckout = useCallback(() => {
    if (!isEnabled) return;
    sendEvent({ event: EVENT_NAME.GUEST_CHECKOUT });
  }, [isEnabled, sendEvent]);

  const sendEcommerceEventBySkus = useCallback(
    async ({
      event,
      skus,
      itemListName,
      itemIndex,
      eventExtras,
      preOrderSkus,
    }: {
      event: EcommerceEventName;
      skus: string[];
      itemListName?: string;
      itemIndex?: number;
      eventExtras?: Record<string, unknown>;
      preOrderSkus?: string[];
    }) => {
      if (!isEnabled || skus.length === 0) return;

      let skusToFetch = skus.filter((sku) => !!sku);
      if (skusToFetch.length === 0) return;

      if (event === EVENT_NAME.VIEW_ITEM_LIST) {
        skusToFetch = skusToFetch.filter((sku) => !pushedListSkusRef.current.has(sku));
        if (skusToFetch.length === 0) return;
        skusToFetch.forEach((sku) => pushedListSkusRef.current.add(sku));
      }

      const fire = async () => {
        try {
          const ecommerce = await fetchEcommerceBySkus({ locale, skus: skusToFetch, itemListName });
          if (itemIndex !== undefined && ecommerce.items?.[0]) {
            ecommerce.items[0].index = itemIndex;
          }

          const stampAllPreOrder =
            !preOrderSkus?.length &&
            (isPreOrderCta(
              typeof eventExtras?.cta_type === 'string' ? eventExtras.cta_type : undefined
            ) ||
              eventExtras?.order_type === 'PRE_ORDER');

          if (stampAllPreOrder) {
            ecommerce.items = stampPreOrderItems(ecommerce.items);
          } else if (preOrderSkus?.length) {
            ecommerce.items = stampPreOrderItems(ecommerce.items, preOrderSkus);
          }

          const emailHash = hashedEmail ?? (await hashEmail(getUserFromCookie().email));
          const isRegisteredWatch = event === EVENT_NAME.REGISTERED_WATCH;
          const isDiscoveryEvent =
            event === EVENT_NAME.VIEW_ITEM ||
            event === EVENT_NAME.VIEW_ITEM_LIST ||
            event === EVENT_NAME.SELECT_ITEM;
          const omitTopLevelValueAndCurrency =
            isDiscoveryEvent ||
            event === EVENT_NAME.ADD_TO_WISHLIST ||
            event === EVENT_NAME.CALL_TO_BUY;
          const payload: EcommercePayload = isRegisteredWatch
            ? {
                items: (ecommerce.items ?? []).map((item) => ({
                  item_id: item.item_id,
                  item_name: item.item_name,
                  item_category: item.item_category,
                  item_collection: item.item_collection,
                  item_gender: item.item_gender,
                })) as EcommercePayload['items'],
              }
            : omitTopLevelValueAndCurrency
              ? omitEcommerceValueAndCurrency(ecommerce)
              : ecommerce;

          sendEcommerceEvent({
            event,
            ...eventExtras,
            ...(emailHash && { user_hashed_email: emailHash }),
            ...(isRegisteredWatch && { page_type: PAGE_TYPE.MY_ACCOUNT_MY_WATCHES }),
            ecommerce: payload,
          });
        } catch (error) {
          if (event === EVENT_NAME.VIEW_ITEM_LIST) {
            skusToFetch.forEach((sku) => pushedListSkusRef.current.delete(sku));
          }
          devError(`[GTM] ${event} by SKUs failed:`, error);
        }
      };

      if (event === EVENT_NAME.VIEW_ITEM || event === EVENT_NAME.VIEW_ITEM_LIST) {
        const timerRef = event === EVENT_NAME.VIEW_ITEM ? viewItemTimerRef : viewItemListTimerRef;
        if (timerRef.current !== null) {
          clearTimeout(timerRef.current);
        }
        timerRef.current = setTimeout(() => {
          timerRef.current = null;
          void fire();
        }, GA_ECOMMERCE_EVENT_DELAY_MS);
        return;
      }

      await fire();
    },
    [isEnabled, locale, sendEcommerceEvent, hashedEmail]
  );

  const sendViewItemList = useCallback(
    (products: ProductCardData[], itemListName?: string) => {
      const skus = products
        .map((product) => product.sku)
        .filter((sku): sku is string => Boolean(sku));
      const preOrderSkus = products
        .filter((product) => isPreOrderCta(product.ctaType) && product.sku)
        .map((product) => product.sku);

      void sendEcommerceEventBySkus({
        event: EVENT_NAME.VIEW_ITEM_LIST,
        skus,
        itemListName,
        ...(preOrderSkus.length > 0 ? { preOrderSkus } : {}),
      });
    },
    [sendEcommerceEventBySkus]
  );

  const sendNotifyMeEvent = useCallback(
    async (itemId: string) => {
      if (!itemId) return;
      await sendEcommerceEventBySkus({
        event: EVENT_NAME.NOTIFY_ME,
        skus: [itemId],
      });
    },
    [sendEcommerceEventBySkus]
  );

  const sendAvailabilityRequestEvent = useCallback(
    async (sku: string, phase: 'submit' | 'success', storeId?: string) => {
      if (!sku) return;
      if (phase === 'submit') {
        await sendEcommerceEventBySkus({
          event: EVENT_NAME.AVAILABILITY_REQUEST_SUBMIT,
          skus: [sku],
          eventExtras: storeId ? { store_id: storeId } : undefined,
        });
        return;
      }
      await sendEcommerceEventBySkus({
        event: EVENT_NAME.AVAILABILITY_REQUEST,
        skus: [sku],
        eventExtras: storeId ? { store_id: storeId } : undefined,
      });
      await sendGenerateLead({ storeId, sku });
    },
    [sendEcommerceEventBySkus, sendGenerateLead]
  );

  const sendCallToBuyEvent = useCallback(
    async (sku: string) => {
      if (!sku) return;
      await sendEcommerceEventBySkus({
        event: EVENT_NAME.CALL_TO_BUY,
        skus: [sku],
      });
    },
    [sendEcommerceEventBySkus]
  );

  return useMemo(() => {
    if (!isEnabled) return NOOP_HOOK;
    return {
      sendPageView,
      sendPopinEvent,
      sendNewsletterEvent,
      sendCtaDiscoverEvent,
      sendVideoEvent,
      sendSearchEvent,
      sendLoginEvent,
      sendSignUpEvent,
      sendContactEvent,
      sendCallToBuyEvent,
      sendGenerateLead,
      sendBookAnAppt,
      sendNotifyMeEvent,
      sendAvailabilityRequestEvent,
      sendPromotionEvent,
      sendIntentionEvent,
      sendViewItem,
      sendViewItemList,
      sendSelectItem,
      sendAddToCart,
      sendRemoveFromCart,
      sendIntentionViewCart,
      sendViewCart,
      sendBeginCheckout,
      sendAddShippingInfo,
      sendReviewOrder,
      sendPurchase,
      sendGuestCheckout,
      sendAddPaymentInfo,
      sendEcommerceFromCheckoutLines,
      sendEcommerceEventBySkus,
    };
  }, [
    isEnabled,
    sendPageView,
    sendPopinEvent,
    sendNewsletterEvent,
    sendCtaDiscoverEvent,
    sendVideoEvent,
    sendSearchEvent,
    sendLoginEvent,
    sendSignUpEvent,
    sendContactEvent,
    sendCallToBuyEvent,
    sendGenerateLead,
    sendBookAnAppt,
    sendNotifyMeEvent,
    sendAvailabilityRequestEvent,
    sendPromotionEvent,
    sendIntentionEvent,
    sendViewItem,
    sendViewItemList,
    sendSelectItem,
    sendAddToCart,
    sendRemoveFromCart,
    sendIntentionViewCart,
    sendViewCart,
    sendBeginCheckout,
    sendAddShippingInfo,
    sendAddPaymentInfo,
    sendReviewOrder,
    sendPurchase,
    sendGuestCheckout,
    sendEcommerceFromCheckoutLines,
    sendEcommerceEventBySkus,
  ]);
}
